// START IMPORTS
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// Theme and UI imports
import { Stack, Box } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  ReportFactory,
  ReportLoading,
  ReportOptions,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  PotentialOwners as PotentialOwnersV2,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  UsernameReportOverview,
  ReportNullState,
  ReportActionsWithDateUpdated,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getSocialNavLinkData } from 'navLinkData/socialNavLinkData';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface SocialReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'username';
// END CONSTANTS

export function SocialReportPage({ permalink }: SocialReportProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const { hash } = useLocation();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  useScrollToSectionOnNavigate(hash, queryResult);

  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={9} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'username');

  if (!report) return <ReportLoading menuItems={9} />;

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    relatives,
  } = owner;

  const socialNavLinkData = getSocialNavLinkData(
    owner,
    report?.data?.people?.length ?? 0
  );

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  document.title = `${report.username} - PeopleSmart`;
  // END DATA_PREPARATION

  // START RENDER

  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Social Report"
            headerTitle={ownerName || report.username}
            menuItems={
              socialNavLinkData as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_ACTIONS */}
          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />
          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Username"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}
          <UsernameReportOverview
            dateUpdated={reportUpdateDate}
            reportType={reportType}
            username={report.username}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) =>
              handleSetReportOptions({ showHighConfidenceDataOnly: value })
            }
            showUsernameSocialMedias
          />
          {/* END REPORT_OVERVIEW */}

          {/* START POTENTIAL_OWNERS_SECTION */}
          <Box mt={2.5}>
            <PotentialOwnersV2
              potentialOwners={report.data.people || []}
              potentialOwnerIndex={reportOptions.potentialOwnerIndex}
              setPotentialOwnerIndex={(index: number) => {
                handleSetReportOptions({ potentialOwnerIndex: index });
              }}
              personName={ownerName || report.username}
              permalink={permalink}
              reportType={reportType}
            />
          </Box>
          {/* END POTENTIAL_OWNERS_SECTION */}

          {/* START PERSONAL_INFO_SECTIONS */}
          <PhoneSectionV2
            personName={ownerName || report.username}
            phoneList={phones}
            permalink={permalink}
          />
          <EmailSectionV2
            personName={ownerName || report.username}
            emailList={emails}
            permalink={permalink}
          />
          <AddressHistorySectionV2
            addresses={addresses}
            personName={ownerName || report.username}
            permalink={permalink}
            showCityAndStateAddress={false}
            openReportCTA={false}
          />
          <SocialSectionV2
            personName={ownerName || report.username}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          {/* END PERSONAL_INFO_SECTIONS */}

          {/* START ADDITIONAL_INFO_SECTIONS */}
          <JobsSectionV2
            personName={ownerName || report.username}
            jobs={jobs}
            permalink={permalink}
          />
          <EducationSectionV2
            personName={ownerName || report.username}
            educations={educations}
            permalink={permalink}
          />

          <NotesSectionV2 permalink={permalink} />
          <RelatedReportsSectionV2
            reportType="username"
            personName={ownerName || report.username}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
            hasDefaultContactSearch={true}
          />
          <ReportRatingV2
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
          {/* END ADDITIONAL_INFO_SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Social Report"
        headerTitle={ownerName || report.username}
        menuItems={socialNavLinkData as ReportNavigationMenuProps['menuItems']}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
